/**
 * 信用卡分期页（`DR-060` · 批 `104` · B 域 PRD 增补 §4.1 / §4.3）
 *
 * 入口：卡面「分期」按钮（`CreditCardSection`）+ `/installments` 直达。
 *
 * ## 🔴 端侧纪律（本页的边界）
 *
 * - **零口径**：期次生成预演用**同一个**内核函数（`buildInstallmentPeriods`，服务端落库也是它）；
 *   `remainingPrincipal` / `unassignedAmount` / `totalRemainingPrincipal` 全部服务端下发，端侧只渲染；
 * - **金额一律整数分**：输入框用 `yuanTextToCents` / 回显用 `formatMoneyText`（`DR-036` 白名单）；
 * - 预演与请求**同构**：预演算出什么，提交的就是什么（feeMode 透传，不平端侧换算）。
 */

import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccountType,
  buildInstallmentPeriods,
  centsToYuanText,
  formatMoneyText,
  toCents,
  yuanTextToCents,
  type AccountView,
  type InstallmentPlanView,
} from '@jiaying/shared';
import {
  createInstallment,
  deleteInstallment,
  getAccountLedger,
  listAccounts,
  listInstallments,
  payInstallment,
} from '../api/endpoints.js';
// 🆕 批 116：期数档位与手续费文案的**唯一一份** —— 记账页的「这笔分 N 期」共用（`DR-067` §5）
import {
  INSTALLMENT_FEE_MODE_LABELS,
  INSTALLMENT_PERIOD_OPTIONS,
} from '../features/installment-options.js';
import { INSTALLMENTS_HIDE_SETTLED_KEY, readUiFlag, writeUiFlag } from '../features/ui-prefs.js';

/** 建分期表单的草稿（纯状态；校验在提交前由服务端契约 + 本页轻校验承担） */
interface Draft {
  accountId: string;
  principalText: string;
  periods: number;
  feeMode: 'total' | 'per_period';
  feeText: string;
  startBillDay: string;
  sourceIds: string[];
}

/** 「已选来源金额合计」的**纯函数**（预演与「未归属」提示共用，端侧唯一一处加法） */
export function sumSources(
  ledger: readonly { transactionId: string; direction: string; amount: number }[],
  sourceIds: readonly string[],
): number {
  const byId = new Map(ledger.map((l) => [l.transactionId, l]));
  return sourceIds.reduce((a, id) => {
    const row = byId.get(id);
    return row !== undefined && row.direction === 'out' ? a + row.amount : a;
  }, 0);
}

export function InstallmentsPage(): ReactElement {
  const qc = useQueryClient();
  const navigate = useNavigate();
  /**
   * 🆕 批 `128`：`/installments/:id` 的**深链定位**。
   *
   * 🔴 修的是一个真实的断链：路由（`AppRoutes.tsx`）早就声明了 `/installments/:id`，
   * 流水页的「已分 N 期」Chip 也**真的会跳到这里**，而本页**从来没过参数**
   * ⇒ 用户点了之后只是回到列表顶端，还得自己在一屏里找那条计划（"以为跳错了"）。
   *
   * 现在的处置：命中的那张卡 `data-focused="true"` + 自动展开溯源 + `scrollIntoView`。
   * ⚠️ 静态渲染基座跑不到 `useEffect` ⇒ 测试只锁 `data-focused`，滚动由真机冒烟取证。
   */
  const params = useParams();
  const focusId = params.id;
  const installments = useQuery({ queryKey: ['installments'], queryFn: listInstallments });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  /**
   * 🆕 批 `128`：隐藏已结清（`AC-B2.19` 的端侧纪律按新口径改写 —— 见 changelog）。
   * 默认**隐藏**，偏好存 `localStorage`（`features/ui-prefs.ts`）。
   */
  const [hideSettled, setHideSettled] = useState(() => readUiFlag(INSTALLMENTS_HIDE_SETTLED_KEY, true));

  /** 打开某一笔原始流水（`?txn=` 深链 → 流水页抽屉） */
  const openTxn = (transactionId: string): void => {
    navigate(`/journal?txn=${encodeURIComponent(transactionId)}`);
  };

  const [createOpen, setCreateOpen] = useState(false);
  const [payTarget, setPayTarget] = useState<InstallmentPlanView | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InstallmentPlanView | null>(null);

  const cards = (accounts.data?.items ?? []).filter((a) => a.type === AccountType.Credit);
  const payAccounts = (accounts.data?.items ?? []).filter(
    (a) => a.type === AccountType.Cash || a.type === AccountType.Debit || a.type === AccountType.Ewallet,
  );

  const payMut = useMutation({
    mutationFn: (v: { planId: string; accountId: string }) =>
      payInstallment(v.planId, { accountId: v.accountId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['installments'] });
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      void qc.invalidateQueries({ queryKey: ['creditBills'] });
      setPayTarget(null);
    },
  });
  const delMut = useMutation({
    mutationFn: (planId: string) => deleteInstallment(planId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['installments'] });
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      void qc.invalidateQueries({ queryKey: ['creditBills'] });
      setDeleteTarget(null);
    },
  });

  if (installments.isPending || accounts.isPending) {
    return <LinearProgress data-testid="installments-loading" />;
  }
  if (installments.isError) {
    return <Alert severity="error">分期列表加载失败：{String(installments.error)}</Alert>;
  }

  const items = installments.data.items;
  const settledCount = items.filter((p) => p.status === 'settled').length;

  /**
   * 🔴 隐藏例外：深链指名的正是**已结清**的那一条 ⇒ 必须放行（否则"点了链接却看不见"）。
   * 其余情况按开关过滤；服务端已把已结清排到末尾（`loadPlans` 的 `ORDER BY`），端侧只做过滤、**不重排**。
   */
  const focusedSettled =
    focusId !== undefined && items.some((p) => p.id === focusId && p.status === 'settled');
  const visible = !hideSettled || focusedSettled ? items : items.filter((p) => p.status !== 'settled');

  return (
    <Stack spacing={2} data-testid="installments-page">
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            信用卡分期
          </Typography>
          <Typography variant="caption" color="text.secondary">
            负债只计未还本金（不含手续费）· 手续费每期一笔支出 · 消费在刷卡那天照常计支出
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setCreateOpen(true)} data-testid="installment-create">
          登记分期
        </Button>
      </Stack>

      {/*
        🆕 批 `128`：隐藏已结清的开关。
        🔴 只有真的存在已结清计划时才出现 —— 一条都没有时它是个没有作用的控件，
        而"点不动的控件"比"没有控件"更让人困惑（铁律：不画点不动的灰项）。
      */}
      {settledCount === 0 ? null : (
        <FormControlLabel
          sx={{ ml: 0, mt: -0.5 }}
          control={
            <Switch
              size="small"
              checked={hideSettled}
              onChange={(e) => {
                setHideSettled(e.target.checked);
                writeUiFlag(INSTALLMENTS_HIDE_SETTLED_KEY, e.target.checked);
              }}
              data-testid="installments-hide-settled"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              {hideSettled ? `已折叠 ${settledCount} 笔已结清` : '隐藏已结清'}
            </Typography>
          }
        />
      )}

      {items.length === 0 ? (
        <Alert severity="info" data-testid="installments-empty">
          还没有分期。在信用卡卡面点「分期」，或点右上角「登记分期」。
        </Alert>
      ) : visible.length === 0 ? (
        /* 全部已结清且被折叠 —— 必须说清楚"不是没有"，并给回退动作 */
        <Alert severity="info" data-testid="installments-all-settled">
          这 {settledCount} 笔分期都已结清，已按你的设置折叠。打开上面的开关可以查看它们。
        </Alert>
      ) : (
        visible.map((p) => (
          <PlanCard
            key={p.id}
            plan={p}
            focused={p.id === focusId}
            onOpenTxn={openTxn}
            onPay={() => setPayTarget(p)}
            onDelete={() => setDeleteTarget(p)}
          />
        ))
      )}

      <CreateDialog
        open={createOpen}
        cards={cards}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          void qc.invalidateQueries({ queryKey: ['installments'] });
          void qc.invalidateQueries({ queryKey: ['accounts'] });
          void qc.invalidateQueries({ queryKey: ['creditBills'] });
        }}
      />

      {/* ── 还本期 ── */}
      <Dialog open={payTarget !== null} onClose={() => setPayTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>还本期</DialogTitle>
        <DialogContent>
          {payTarget !== null && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Typography variant="body2">
                {payTarget.name} · 第 {(payTarget.paidPeriods ?? 0) + 1}/{payTarget.periods} 期
              </Typography>
              <Typography variant="body2" data-testid="pay-summary">
                本金 {formatMoneyText(toCents(payTarget.nextDuePrincipal ?? 0), payAccountCurrency(payAccounts, payTarget))}{' '}
                + 手续费 {formatMoneyText(toCents(payTarget.nextDueFee ?? 0), payAccountCurrency(payAccounts, payTarget))}{' '}
                = {formatMoneyText(
                  toCents((payTarget.nextDuePrincipal ?? 0) + (payTarget.nextDueFee ?? 0)),
                  payAccountCurrency(payAccounts, payTarget),
                )}
              </Typography>
              <PayAccountPicker accounts={payAccounts} onPick={(id) => payMut.mutate({ planId: payTarget.id, accountId: id })} busy={payMut.isPending} />
              {payMut.isError && <Alert severity="error">{String(payMut.error)}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayTarget(null)}>取消</Button>
        </DialogActions>
      </Dialog>

      {/* ── 删除确认 ── */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>删除分期计划？</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {deleteTarget?.name} · 未还本金 {formatMoneyText(toCents(deleteTarget?.remainingPrincipal ?? 0), 'CNY')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            已还过的期次无法删除（需先撤销还款）；未还的计划删除后期次同删、净资产回到办理前。
          </Typography>
          {delMut.isError && <Alert severity="error" sx={{ mt: 1 }}>{String(delMut.error)}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>取消</Button>
          <Button
            color="error"
            data-testid="installment-delete-confirm"
            onClick={() => deleteTarget !== null && delMut.mutate(deleteTarget.id)}
          >
            删除
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function payAccountCurrency(accounts: readonly AccountView[], plan: InstallmentPlanView): string {
  // 分期跟随卡币种（PRD §3.2：多币种分期不做）—— 期次金额按卡币种渲染
  return accounts.find((a) => a.id === plan.accountId)?.currency ?? 'CNY';
}

// ─────────────────────────── 计划卡 ───────────────────────────

function PlanCard(props: {
  plan: InstallmentPlanView;
  /** 🆕 批 `128`：深链 `/installments/:id` 命中的那一条 */
  focused: boolean;
  /** 🆕 批 `128`：打开关联的原始流水（`?txn=` 深链） */
  onOpenTxn: (transactionId: string) => void;
  onPay: () => void;
  onDelete: () => void;
}): ReactElement {
  const { plan, focused } = props;
  /** 深链命中的卡**自动展开溯源**（用户跳过来就是要看这笔的来龙去脉） */
  const [expanded, setExpanded] = useState(focused);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const paid = plan.paidPeriods;
  const progress = plan.periods === 0 ? 0 : Math.round((paid / plan.periods) * 100);

  /**
   * 滚动到视口中间。⚠️ 静态渲染基座跑不到 effect ⇒ 这一条只能由**真机冒烟**取证，
   * 测试断言的是 `data-focused`（判据 = 属性在不在，不是"滚没滚"）。
   */
  useEffect(() => {
    if (!focused) return;
    boxRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focused]);

  return (
    <Box
      ref={boxRef}
      data-testid={`installment-plan-${plan.id}`}
      data-focused={focused ? 'true' : undefined}
      sx={{
        border: 1,
        // 深链命中 ⇒ 主色边框（与列表里其它卡一眼区分）
        borderColor: focused ? 'primary.main' : 'divider',
        borderRadius: 2,
        p: 2,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
            {plan.name ?? '信用卡分期'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {plan.accountName} · {plan.principalTotal > 0 ? '' : ''}
            本金 {formatMoneyText(toCents(plan.principalTotal), 'CNY')} · {plan.periods} 期
            {plan.feeTotal > 0 ? ` · 手续费 ${formatMoneyText(toCents(plan.feeTotal), 'CNY')}` : ' · 免息'}
          </Typography>
        </Box>
        <Chip
          size="small"
          variant="outlined"
          label={plan.status === 'settled' ? '已结清' : `还剩 ${plan.remainingPeriods} 期`}
          color={plan.status === 'settled' ? 'default' : 'primary'}
        />
      </Stack>

      <Box sx={{ mt: 1 }}>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          进度 {paid}/{plan.periods} 期 · 分期未还{' '}
          <strong data-testid={`installment-remaining-${plan.id}`}>
            {formatMoneyText(toCents(plan.remainingPrincipal), 'CNY')}
          </strong>
          {plan.nextDueDate !== undefined
            ? ` · 下期 ${plan.nextDueDate}（${formatMoneyText(toCents((plan.nextDuePrincipal ?? 0) + (plan.nextDueFee ?? 0)), 'CNY')}）`
            : ''}
          {plan.estimatedEndBill !== null && plan.status === 'active' ? ` · 预计 ${plan.estimatedEndBill.slice(0, 7)} 结清` : ''}
        </Typography>
      </Box>

      {/* 溯源块（`AC-B2.23`） */}
      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }} alignItems="center">
        <Chip
          size="small"
          label={plan.srcKind === 'txn' ? `关联 ${plan.sources.length} 笔消费` : '账单金额分期'}
          onClick={() => setExpanded((v) => !v)}
          data-testid={`installment-sources-toggle-${plan.id}`}
        />
        {plan.unassignedAmount > 0 && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`未归属 ${formatMoneyText(toCents(plan.unassignedAmount), 'CNY')}`}
          />
        )}
      </Stack>

      {expanded && (
        <Box sx={{ mt: 1 }}>
          <Divider sx={{ mb: 1 }} />
          {plan.sources.length > 0 ? (
            plan.sources.map((s) => (
              <Stack
                key={s.transactionId}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ py: 0.25 }}
              >
                <Typography variant="caption" color="text.secondary">
                  {s.date} · {s.merchant ?? '（无备注）'}
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Typography variant="caption">{formatMoneyText(toCents(s.amount), 'CNY')}</Typography>
                  {/*
                    🆕 批 `128`：来源消费 → 原始流水（`?txn=` 深链直开抽屉）。
                    🔴 用抽屉而不是"筛选到那一笔"：分期可能挂在去年的消费上，
                    默认近 3 月的列表里根本没有它（筛选永远打不开）。
                  */}
                  <Button
                    size="small"
                    sx={{ minWidth: 0, py: 0, px: 0.5, fontSize: '0.72rem' }}
                    onClick={() => props.onOpenTxn(s.transactionId)}
                    data-testid={`installment-source-txn-${s.transactionId}`}
                  >
                    流水
                  </Button>
                </Stack>
              </Stack>
            ))
          ) : (
            <Typography variant="caption" color="text.secondary">
              未关联具体消费（账单金额分期）
            </Typography>
          )}
          <Divider sx={{ my: 1 }} />
          {plan.schedule.map((s) => (
            <Stack key={s.periodIndex} direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary">
                第 {s.periodIndex + 1} 期 · {s.dueDate}
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography variant="caption">
                  本金 {formatMoneyText(toCents(s.principal), 'CNY')} + 手续费{' '}
                  {formatMoneyText(toCents(s.fee), 'CNY')} · {s.status === 'paid' ? '已还' : '待还'}
                </Typography>
                {/* 已还期次 → 这笔还款流水（`paid_transaction_id` 是期次表上的唯一持久关联） */}
                {s.status === 'paid' && s.paidTransactionId !== null ? (
                  <Button
                    size="small"
                    sx={{ minWidth: 0, py: 0, px: 0.5, fontSize: '0.72rem' }}
                    onClick={() => props.onOpenTxn(s.paidTransactionId as string)}
                    data-testid={`installment-period-txn-${plan.id}-${s.periodIndex}`}
                  >
                    还款流水
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          ))}
        </Box>
      )}

      {plan.status === 'active' && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button size="small" variant="contained" onClick={props.onPay} data-testid={`installment-pay-${plan.id}`}>
            还本期
          </Button>
          <Button size="small" color="error" onClick={props.onDelete}>
            删除
          </Button>
        </Stack>
      )}
    </Box>
  );
}

// ─────────────────────────── 建分期对话框 ───────────────────────────

function CreateDialog(props: {
  open: boolean;
  cards: readonly AccountView[];
  onClose: () => void;
  onCreated: () => void;
}): ReactElement {
  const firstCard = props.cards[0];
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 打开时初始化草稿（受控：值都在页面里 —— 端侧测试基座的前提）
  const d: Draft =
    draft ??
    {
      accountId: firstCard?.id ?? '',
      principalText: '',
      periods: 12,
      feeMode: 'total',
      feeText: '',
      startBillDay: '',
      sourceIds: [],
    };

  const card = props.cards.find((c) => c.id === d.accountId);
  const cardLedger = useQuery({
    queryKey: ['installment-source-candidates', d.accountId],
    queryFn: () => getAccountLedger(d.accountId, 60),
    enabled: props.open && d.accountId !== '',
  });
  const candidates = (cardLedger.data?.items ?? []).filter((l) => l.direction === 'out');

  const principalCents = d.principalText === '' ? 0 : yuanTextToCents(d.principalText);
  const feeCents = d.feeText === '' ? 0 : yuanTextToCents(d.feeText);
  const sourcesSum = useMemo(
    () => sumSources(candidates, d.sourceIds),
    [candidates, d.sourceIds],
  );

  // 🔴 预演与落库**同一个内核函数**（端侧不算第二遍口径）
  const preview = useMemo(() => {
    if (principalCents <= 0 || d.periods < 2) return null;
    const feeTotal = d.feeMode === 'total' ? feeCents : feeCents * d.periods;
    try {
      return {
        periods: buildInstallmentPeriods({
          principalTotal: toCents(principalCents),
          periods: d.periods,
          feeTotal: toCents(feeTotal),
          // 预演的到期日序列只需要相对形状；startBillDay 未选时用占位日（显示层只展示每期金额）
          startBillDay: d.startBillDay === '' ? '2026-01-01' : d.startBillDay,
          billingDay: card?.credit?.billingDay ?? 1,
        }),
        feeTotal,
      };
    } catch {
      return null;
    }
  }, [principalCents, feeCents, d.periods, d.feeMode, d.startBillDay, card]);

  const createMut = useMutation({
    mutationFn: () =>
      createInstallment({
        accountId: d.accountId,
        principalTotal: principalCents,
        periods: d.periods,
        feeMode: d.feeMode,
        feeValue: feeCents,
        startBillDay: d.startBillDay,
        ...(d.sourceIds.length > 0
          ? {
              sources: d.sourceIds.map((id) => ({
                transactionId: id,
                amount: candidates.find((c) => c.transactionId === id)?.amount ?? 0,
              })),
            }
          : {}),
      }),
    onSuccess: () => {
      setDraft(null);
      setError(null);
      props.onCreated();
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const canSubmit =
    d.accountId !== '' && principalCents > 0 && d.periods >= 2 && d.startBillDay !== '';

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
      <DialogTitle>登记分期</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }} data-testid="installment-create-form">
          <TextField
            select
            label="信用卡"
            value={d.accountId}
            onChange={(e) => setDraft({ ...d, accountId: e.target.value, sourceIds: [] })}
            data-testid="installment-account"
          >
            {props.cards.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction="row" spacing={2}>
            <TextField
              label="本金（元）"
              value={d.principalText}
              onChange={(e) => setDraft({ ...d, principalText: e.target.value })}
              fullWidth
              data-testid="installment-principal"
            />
            <TextField
              select
              label="期数"
              value={String(d.periods)}
              onChange={(e) => setDraft({ ...d, periods: Number(e.target.value) })}
              sx={{ minWidth: 110 }}
              data-testid="installment-periods"
            >
              {INSTALLMENT_PERIOD_OPTIONS.map((n) => (
                <MenuItem key={n} value={String(n)}>
                  {String(n)} 期
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            <RadioGroup
              row
              value={d.feeMode}
              onChange={(e) => setDraft({ ...d, feeMode: e.target.value as 'total' | 'per_period' })}
            >
              <FormControlLabel value="total" control={<Radio />} label={INSTALLMENT_FEE_MODE_LABELS.total} />
              <FormControlLabel
                value="per_period"
                control={<Radio />}
                label={INSTALLMENT_FEE_MODE_LABELS.per_period}
              />
            </RadioGroup>
            <TextField
              label={d.feeMode === 'total' ? '总手续费（元）' : '每期手续费（元）'}
              value={d.feeText}
              onChange={(e) => setDraft({ ...d, feeText: e.target.value })}
              fullWidth
              data-testid="installment-fee"
            />
          </Stack>

          <TextField
            label="首期账单日"
            type="date"
            value={d.startBillDay}
            onChange={(e) => setDraft({ ...d, startBillDay: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
            data-testid="installment-start"
            helperText={card?.credit?.billingDay != null ? `该卡账单日为每月 ${String(card.credit.billingDay)} 号（短月按月末）` : undefined}
          />

          {/* 关联消费（可多笔 · `DR-060` D5 溯源） */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              关联来源消费（可多笔；不选 = 账单金额分期）
            </Typography>
            {candidates.slice(0, 12).map((l) => (
              <FormControlLabel
                key={l.transactionId}
                control={
                  <input
                    type="checkbox"
                    checked={d.sourceIds.includes(l.transactionId)}
                    onChange={(e) =>
                      setDraft({
                        ...d,
                        sourceIds: e.target.checked
                          ? [...d.sourceIds, l.transactionId]
                          : d.sourceIds.filter((x) => x !== l.transactionId),
                      })
                    }
                  />
                }
                label={`${l.date} · ${l.note ?? '（无备注）'} · ${formatMoneyText(toCents(l.amount), card?.currency ?? 'CNY')}`}
                sx={{ display: 'flex' }}
                data-testid={`installment-source-${l.transactionId}`}
              />
            ))}
            {d.sourceIds.length > 0 && (
              <Typography variant="caption" display="block" data-testid="installment-unassigned">
                已选 {formatMoneyText(toCents(sourcesSum), card?.currency ?? 'CNY')} · 未归属{' '}
                {formatMoneyText(toCents(Math.max(0, principalCents - sourcesSum)), card?.currency ?? 'CNY')}
              </Typography>
            )}
          </Box>

          {/* 预演（与落库同一内核） */}
          {preview !== null && preview.periods.length > 0 && (
            <Alert severity="info" data-testid="installment-preview">
              预演：每期本金 {centsToYuanText(preview.periods[0]!.principal)} + 手续费{' '}
              {centsToYuanText(preview.periods[0]!.fee)} × {String(d.periods)} 期 · 合计还款{' '}
              {centsToYuanText(
                toCents(
                  preview.periods.reduce((a, p) => a + (p.principal as number) + (p.fee as number), 0),
                ),
              )}{' '}
              · 其中手续费 {centsToYuanText(toCents(preview.feeTotal))}
            </Alert>
          )}

          {error !== null && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>取消</Button>
        <Button
          variant="contained"
          disabled={!canSubmit || createMut.isPending}
          onClick={() => createMut.mutate()}
          data-testid="installment-submit"
        >
          确认登记
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────── 还款账户选择 ───────────────────────────

function PayAccountPicker(props: {
  accounts: readonly AccountView[];
  onPick: (accountId: string) => void;
  busy: boolean;
}): ReactElement {
  const [picked, setPicked] = useState('');
  return (
    <Stack direction="row" spacing={1}>
      <TextField
        select
        label="付款账户"
        value={picked}
        onChange={(e) => setPicked(e.target.value)}
        fullWidth
        size="small"
        data-testid="pay-account"
      >
        {props.accounts.map((a) => (
          <MenuItem key={a.id} value={a.id}>
            {a.name}
          </MenuItem>
        ))}
      </TextField>
      <Button
        variant="contained"
        disabled={picked === '' || props.busy}
        onClick={() => props.onPick(picked)}
        data-testid="pay-confirm"
      >
        确认还款
      </Button>
    </Stack>
  );
}
